import { useEffect, useRef, useState } from 'react';
import Icon from '../ui/Icon';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const STEP = 0.5;

function ToolButton({ icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-on-surface-variant hover:bg-surface-container hover:text-on-surface disabled:opacity-40 disabled:cursor-not-allowed outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
    >
      <Icon name={icon} size={20} />
    </button>
  );
}

/**
 * Receipt photo with zoom in / out / fit / rotate (CSS transforms) and drag-to-pan while
 * zoomed. At fit size a click opens the full image in a new tab.
 */
export default function ReceiptViewer({ url, receiptNumber }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [state, setState] = useState(url ? 'loading' : 'missing');
  const drag = useRef(null);

  useEffect(() => {
    setState(url ? 'loading' : 'missing');
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, [url]);

  const setZoomTo = (z) => {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
    setZoom(next);
    if (next === 1) setPan({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    if (zoom === 1) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, pan, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) drag.current.moved = true;
    setPan({ x: drag.current.pan.x + dx, y: drag.current.pan.y + dy });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <section aria-label="Receipt photo" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-surface-container-low">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-on-surface min-w-0">
          <Icon name="receipt_long" size={20} className="text-primary shrink-0" />
          <span className="truncate">
            Ticket <span className="font-data-mono">#{receiptNumber || '—'}</span>
          </span>
        </h2>
        <div className="flex items-center gap-0.5 bg-surface-container-lowest rounded-lg p-0.5 shadow-xs">
          <ToolButton icon="zoom_in" label="Zoom in" onClick={() => setZoomTo(zoom + STEP)} disabled={state !== 'ready' || zoom >= MAX_ZOOM} />
          <ToolButton icon="zoom_out" label="Zoom out" onClick={() => setZoomTo(zoom - STEP)} disabled={state !== 'ready' || zoom <= MIN_ZOOM} />
          <ToolButton
            icon="fit_screen"
            label="Fit to view"
            onClick={() => {
              setZoomTo(1);
              setRotation(0);
            }}
            disabled={state !== 'ready'}
          />
          <ToolButton icon="rotate_right" label="Rotate 90°" onClick={() => setRotation((r) => (r + 90) % 360)} disabled={state !== 'ready'} />
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open full image in a new tab"
              title="Open full image"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-on-surface-variant hover:bg-surface-container hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              <Icon name="open_in_new" size={18} />
            </a>
          )}
        </div>
      </div>

      <div
        className={`relative h-[560px] rounded-xl bg-surface-container overflow-hidden flex items-center justify-center select-none ${
          zoom > 1 ? 'cursor-grab active:cursor-grabbing' : state === 'ready' ? 'cursor-zoom-in' : ''
        }`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {state === 'missing' && (
          <p className="flex flex-col items-center gap-2 text-sm text-on-surface-variant">
            <Icon name="hide_image" size={36} />
            No receipt photo
          </p>
        )}
        {state === 'broken' && (
          <p className="flex flex-col items-center gap-2 text-sm text-on-surface-variant text-center px-6">
            <Icon name="broken_image" size={36} />
            The receipt photo couldn&apos;t be loaded.
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary font-semibold hover:underline">
                Try opening it directly
              </a>
            )}
          </p>
        )}
        {state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center" role="status" aria-label="Loading receipt photo">
            <Icon name="progress_activity" size={28} className="animate-spin text-secondary" />
          </div>
        )}
        {url && state !== 'broken' && (
          <img
            src={url}
            alt={`Receipt ${receiptNumber ? `#${receiptNumber}` : 'photo'}`}
            referrerPolicy="no-referrer"
            draggable={false}
            onLoad={() => setState('ready')}
            onError={() => setState('broken')}
            onClick={() => {
              if (zoom === 1 && state === 'ready') window.open(url, '_blank', 'noopener,noreferrer');
            }}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)` }}
            className={`max-w-full max-h-full object-contain transition-transform duration-150 ${state === 'loading' ? 'opacity-0' : ''}`}
          />
        )}
      </div>
    </section>
  );
}
